import { AnimatePresence, motion } from "framer-motion"
import { createPortal } from "react-dom"
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import * as XLSX from "xlsx"
import { saveAs } from "file-saver"

import useAppStore from "../../../store/useAppStore"
import useLayoutStore from "../../../store/useLayoutStore"
import LoadingSpinner from "../../elements/LoadingSpinner"

const MONTH_NAMES = [
    "Januar", "Februar", "Mart", "April", "Maj", "Jun",
    "Jul", "Avgust", "Septembar", "Oktobar", "Novembar", "Decembar"
]

const PAGE_SIZE = 50

const LOG_STATUS_ARROW = {
    ulaz: { color: "#16a34a", label: "Ulaz", path: "M5 12h12M13 8l4 4-4 4" },
    izlaz: { color: "#dc2626", label: "Izlaz", path: "M19 12H7M11 8l-4 4 4 4" }
}

const LogStatusIcon = ({ status }) => {
    const cfg = LOG_STATUS_ARROW[status]
    if (!cfg) {
        const text = status && String(status).trim() ? status : "—"
        return <span>{text}</span>
    }
    return (
        <span className="asv-emp-day-log-status-icon" title={cfg.label} aria-label={cfg.label}>
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
                <path
                    d={cfg.path}
                    fill="none"
                    stroke={cfg.color}
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </span>
    )
}

const formatDatum = (log) => {
    if (log?.datum && String(log.datum).trim()) return log.datum
    if (log?.dan && log?.mesec && log?.godina) {
        return `${String(log.dan).padStart(2, "0")}.${String(log.mesec).padStart(2, "0")}.${log.godina}`
    }
    return "—"
}

const formatCell = (value) => {
    if (value == null) return "—"
    const text = String(value).trim()
    return text || "—"
}

const formatIzlazMinutes = (log) => {
    if (log?.status !== "izlaz") return "—"
    const mins = log.min_izmedju_upisa
    if (mins == null || Number.isNaN(Number(mins))) return "—"
    return String(mins)
}

const getLogRowKey = (log, idx) =>
    log._id ? String(log._id) : `row-${idx}-${log.user_id}-${log.vreme}-${log.status}`

const formatCellExport = (value) => {
    if (value == null) return ""
    const text = String(value).trim()
    return text || ""
}

const logToExcelRow = (log) => [
    formatCellExport(log.ime),
    formatCellExport(log.prezime),
    formatDatum(log) === "—" ? "" : formatDatum(log),
    formatCellExport(log.vreme),
    formatCellExport(log.status),
    formatCellExport(log.tipPotvrda),
    formatCellExport(log.korisnikPotvrda),
    formatCellExport(log.izmenio),
    formatIzlazMinutes(log) === "—" ? "" : formatIzlazMinutes(log)
]

const flattenStatsMonthReport = (groupedData) => {
    if (!groupedData || typeof groupedData !== "object") return []

    const rows = []
    for (const logs of Object.values(groupedData)) {
        if (!Array.isArray(logs)) continue
        for (const log of logs) {
            rows.push(log)
        }
    }

    return rows
}

const AdminStatsMonthReportModal = () => {
    const isDarkTheme = useLayoutStore((state) => state.isDarkTheme)
    const isOpen = useAppStore((state) => state.statsMonthReportModalOpen)
    const loading = useAppStore((state) => state.loadingStatsMonthReport)
    const groupedData = useAppStore((state) => state.statsMonthReportData)
    const dateFilter = useAppStore((state) => state.statsMonthReportDateFilter)
    const closeStatsMonthReportModal = useAppStore((state) => state.closeStatsMonthReportModal)

    const modalContainerRef = useRef(null)
    const [page, setPage] = useState(1)

    const allRows = useMemo(() => flattenStatsMonthReport(groupedData), [groupedData])

    const totalPages = Math.max(1, Math.ceil(allRows.length / PAGE_SIZE))

    useEffect(() => {
        setPage(1)
    }, [groupedData, dateFilter.month, dateFilter.year])

    useEffect(() => {
        if (page > totalPages) {
            setPage(totalPages)
        }
    }, [page, totalPages])

    const pageRows = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE
        return allRows.slice(start, start + PAGE_SIZE)
    }, [allRows, page])

    const handleExportExcel = useCallback(() => {
        if (!allRows.length) return

        const headers = [
            "Ime", "Prezime", "Datum", "Vreme", "Status",
            "Tip potvrde", "Potvrdio", "Izmenio", "Min. (izlaz)"
        ]
        const rows = allRows.map(logToExcelRow)
        const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows])
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, "Mesecni izvestaj")

        const monthLabel = MONTH_NAMES[dateFilter.month]
        const fileName = `mesecni_izvestaj_${monthLabel}_${dateFilter.year}.xlsx`
        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" })
        saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), fileName)
    }, [allRows, dateFilter.month, dateFilter.year])

    useLayoutEffect(() => {
        if (!isOpen) return undefined
        const handleClickOutside = (e) => {
            if (modalContainerRef.current && modalContainerRef.current.contains(e.target)) {
                return
            }
            closeStatsMonthReportModal()
        }
        document.addEventListener("mousedown", handleClickOutside, true)
        return () => document.removeEventListener("mousedown", handleClickOutside, true)
    }, [isOpen, closeStatsMonthReportModal])

    const title = `Mesečni izveštaj — ${MONTH_NAMES[dateFilter.month]} ${dateFilter.year}`

    const modalContent = (
        <AnimatePresence>
            {isOpen ? (
                <motion.div
                    key="stats-month-report-modal"
                    className="asv-eLogs-root"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.18, ease: "easeOut" }}
                >
                    <div
                        className={`asv-eLogs-backdrop ${isDarkTheme ? "asv-eLogs-backdrop--dark" : "asv-eLogs-backdrop--light"}`}
                        aria-hidden
                    />
                    <motion.div
                        ref={modalContainerRef}
                        className={`asv-month-report-modal ${isDarkTheme ? "asv-month-report-modal--dark" : "asv-month-report-modal--light"}`}
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.96 }}
                        transition={{ duration: 0.14, ease: "easeOut" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="asv-month-report-modal-inner">
                            <div className="absence-modal-close-buttons-container">
                                <button
                                    type="button"
                                    className={`new-absence-modal-close-button ${isDarkTheme ? "filter-dark-buttons" : ""}`}
                                    onClick={closeStatsMonthReportModal}
                                    aria-label="Zatvori modal"
                                >
                                    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: "block", margin: "auto" }}>
                                        <line x1="7.2" y1="7.2" x2="20.8" y2="20.8" stroke="#08293e" strokeWidth="2.4" strokeLinecap="round" />
                                        <line x1="20.8" y1="7.2" x2="7.2" y2="20.8" stroke="#08293e" strokeWidth="2.4" strokeLinecap="round" />
                                    </svg>
                                </button>
                            </div>

                            <div className={`asv-month-report-modal-header ${isDarkTheme ? "dark-color" : ""}`}>
                                <h2 className="asv-month-report-modal-title">{title}</h2>
                                {!loading && (
                                    <span className="asv-month-report-modal-meta">
                                        {allRows.length} {allRows.length === 1 ? "zapis" : "zapisa"}
                                    </span>
                                )}
                            </div>

                            <div className="asv-month-report-modal-body">
                                {loading ? (
                                    <div className="asv-month-report-modal-loader">
                                        <LoadingSpinner />
                                    </div>
                                ) : allRows.length === 0 ? (
                                    <p className={`asv-month-report-empty ${isDarkTheme ? "dark-color" : ""}`}>
                                        Nema zapisa za izabrani mesec.
                                    </p>
                                ) : (
                                    <>
                                        <div className="asv-month-report-table-scroll">
                                            <table className={`asv-month-report-table ${isDarkTheme ? "asv-month-report-table--dark" : ""}`}>
                                                <thead>
                                                    <tr>
                                                        <th>Ime</th>
                                                        <th>Prezime</th>
                                                        <th>Datum</th>
                                                        <th>Vreme</th>
                                                        <th className="asv-emp-day-logs-table-col-status" aria-label="Status">Status</th>
                                                        <th>Tip potvrde</th>
                                                        <th>Potvrdio</th>
                                                        <th>Izmenio</th>
                                                        <th>Min. (izlaz)</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {pageRows.map((log, idx) => (
                                                        <tr key={getLogRowKey(log, idx)} className="asv-emp-day-log-row">
                                                            <td>{formatCell(log.ime)}</td>
                                                            <td>{formatCell(log.prezime)}</td>
                                                            <td>{formatDatum(log)}</td>
                                                            <td>{formatCell(log.vreme)}</td>
                                                            <td className="asv-emp-day-logs-table-col-status">
                                                                <LogStatusIcon status={log.status} />
                                                            </td>
                                                            <td>{formatCell(log.tipPotvrda)}</td>
                                                            <td>{formatCell(log.korisnikPotvrda)}</td>
                                                            <td>{formatCell(log.izmenio)}</td>
                                                            <td>{formatIzlazMinutes(log)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        <div className={`asv-month-report-pagination ${isDarkTheme ? "asv-month-report-pagination--dark" : ""}`}>
                                            <button
                                                type="button"
                                                className="asv-month-report-page-btn"
                                                disabled={page <= 1}
                                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                            >
                                                Prethodna
                                            </button>
                                            <span className="asv-month-report-page-info">
                                                Strana {page} / {totalPages}
                                            </span>
                                            <button
                                                type="button"
                                                className="asv-month-report-page-btn"
                                                disabled={page >= totalPages}
                                                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                            >
                                                Sledeća
                                            </button>
                                        </div>

                                        <button
                                            type="button"
                                            className={`kv-xlsx-button asv-month-report-export-btn ${isDarkTheme ? "dark-big-btn" : ""}`}
                                            onClick={handleExportExcel}
                                        >
                                            Export u Excel
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            ) : null}
        </AnimatePresence>
    )

    return createPortal(modalContent, document.body)
}

export default AdminStatsMonthReportModal
